import { inject, Injectable, InjectionToken } from '@angular/core';

import { APP_ENVIRONMENT } from '@app/shared/tokens';
import { ICustomProperties } from '@microsoft/applicationinsights-core-js';
import {
    ApplicationInsights,
    IDependencyTelemetry,
    IEventTelemetry,
    IExceptionTelemetry,
    IMetricTelemetry,
    IPageViewTelemetry,
} from '@microsoft/applicationinsights-web';

@Injectable()
export class ApplicationInsightsService {
    private env = inject(APP_ENVIRONMENT);
    private instrumentationKey = inject(INSTRUMENTATION_KEY, { optional: true });

    private appInsights = new ApplicationInsights({
        config: {
            instrumentationKey: this.instrumentationKey || this.env.instrumentationKey,
            appId: this.env.appId,
        },
    });

    constructor() {
        this.appInsights.loadAppInsights();
    }

    addPropertiesToTelemetry(
        event: IEventTelemetry | IPageViewTelemetry | IMetricTelemetry | IDependencyTelemetry | IExceptionTelemetry,
    ) {
        event.properties = event.properties || {};

        if (!event.properties['applicationId']) {
            event.properties['applicationId'] = this.env.appId;
        }

        if (!event.properties['buildVersion']) {
            event.properties['buildVersion'] = this.env.buildVersion;
        }

        if (!event.properties['buildCommit']) {
            event.properties['buildCommit'] = this.env.buildCommit;
        }
        if (!event.properties['production']) {
            event.properties['production'] = this.env.production;
        }
    }

    trackEvent(event: IEventTelemetry, prop?: ICustomProperties) {
        event.properties = event.properties || {};

        this.addPropertiesToTelemetry(event);

        if (this.env.insightsEnabled) {
            this.appInsights.trackEvent({ ...event }, { ...prop });
        }
    }

    trackPageView(event: IPageViewTelemetry) {
        event.properties = event.properties || {};

        this.addPropertiesToTelemetry(event);

        if (this.env.insightsEnabled) {
            this.appInsights.trackPageView({ ...event });
        }
    }

    trackMetric(event: IMetricTelemetry, prop?: ICustomProperties) {
        event.properties = event.properties || {};

        this.addPropertiesToTelemetry(event);

        if (this.env.insightsEnabled) {
            this.appInsights.trackMetric({ ...event }, { ...prop });
        }
    }

    trackDependency(event: IDependencyTelemetry) {
        event.properties = event.properties || {};

        this.addPropertiesToTelemetry(event);

        if (this.env.insightsEnabled) {
            this.appInsights.trackDependencyData({ ...event });
        }
    }

    trackException(ex: IExceptionTelemetry) {
        ex.properties = ex.properties || {};

        this.addPropertiesToTelemetry(ex);

        if (this.env.insightsEnabled) {
            this.appInsights.trackException({ ...ex });
        }
    }

    setAuthenticationContext(userId: string) {
        this.appInsights.setAuthenticatedUserContext(userId, undefined, true);
    }

    clearAuthenticationContext() {
        this.appInsights.clearAuthenticatedUserContext();
    }
}

// Create an InjectionToken for the instrumentation key
export const INSTRUMENTATION_KEY = new InjectionToken<string>('INSTRUMENTATION_KEY');

// // Factory function to create the service with the instrumentation key
// export function createInstrumentationService(instrumentationKey: string): InstrumentationService {
//     return new ApplicationInsightsService(instrumentationKey);
// }
